// ── 상황판 디테일 모달 (모바일 드래그-투-디스미스 지원) ────────────────────────
import React, { useEffect, useRef, useState } from 'react';
import { useBodyScrollLock } from '../../../hooks/useBodyScrollLock';
import { cn } from '../../../lib/utils';
import { nameColor } from './utils';

/**
 * 클릭한 항목의 상세 목록을 보여주는 모달
 * - 모바일: 하단 시트, 위로 드래그하면 닫힘
 * - 데스크탑: 중앙 팝업
 */
export function DetailModal({ detail, onClose, isMobile }) {
  const [dragY, setDragY] = useState(0);
  const dragging  = useRef(false);
  const startY    = useRef(0);
  const dragYRef  = useRef(0);
  const handleRef = useRef(null);
  const DISMISS_THRESHOLD = 110;
  useBodyScrollLock(Boolean(detail));

  // 모바일 터치 드래그 이벤트 등록
  useEffect(() => {
    const el = handleRef.current;
    if (!el || !isMobile) return;
    const onStart = (e) => {
      dragging.current = true;
      startY.current = e.touches[0].clientY;
      dragYRef.current = 0;
      setDragY(0);
    };
    const onMove = (e) => {
      if (!dragging.current) return;
      e.preventDefault();
      const delta = e.touches[0].clientY - startY.current;
      if (delta > 0) { dragYRef.current = delta; setDragY(delta); }
    };
    const onEnd = () => {
      dragging.current = false;
      if (dragYRef.current >= DISMISS_THRESHOLD) { setDragY(0); onClose(); }
      else { setDragY(0); }
      dragYRef.current = 0;
    };
    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove',  onMove,  { passive: false });
    el.addEventListener('touchend',   onEnd,   { passive: true });
    el.addEventListener('touchcancel',onEnd,   { passive: true });
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove',  onMove);
      el.removeEventListener('touchend',   onEnd);
      el.removeEventListener('touchcancel',onEnd);
    };
  }, [isMobile, onClose]);

  // 모바일에서 body 스크롤 잠금
  // ESC 키 닫기
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  if (!detail) return null;
  const { title, rows, count } = detail;
  const progress  = Math.min(dragY / DISMISS_THRESHOLD, 1);
  const isDragging = dragging.current;

  return (
    <>
      {/* 백드롭 */}
      <div
        onClick={onClose}
        className="fixed inset-0 z-[1000] backdrop-blur-sm"
        style={{ background: `rgba(15,27,53,${0.5 * (1 - progress * 0.7)})` }}
      />
      {/* 패널 */}
      <div
        className={cn(
          'fixed z-[1001] flex flex-col overflow-hidden',
          'bg-white dark:bg-pine-900',
          isMobile
            ? 'bottom-0 left-0 right-0 rounded-t-3xl max-h-[80vh]'
            : 'top-1/2 left-1/2 rounded-2xl w-[420px] max-h-[70vh] shadow-popup'
        )}
        style={isMobile
          ? {
              transform: `translateY(${dragY}px)`,
              transition: isDragging ? 'none' : 'transform 0.32s cubic-bezier(0.34,1.56,0.64,1)',
              opacity: 1 - progress * 0.25,
              animation: dragY === 0 ? 'slideUp 0.25s cubic-bezier(0.22,1,0.36,1)' : 'none',
            }
          : { transform: 'translate(-50%, -50%)', animation: 'fadeInScale 0.18s ease' }
        }
      >
        {/* 드래그 핸들 + 헤더 */}
        <div
          ref={isMobile ? handleRef : undefined}
          className={cn('border-b border-[var(--border)] select-none', isMobile && 'cursor-grab')}
        >
          {isMobile && (
            <div className="flex flex-col items-center pt-3 pb-1">
              <div
                className="rounded-full transition-all duration-150"
                style={{ width: progress > 0.3 ? 52 : 40, height: 5, background: progress > 0.4 ? '#9aa3c5' : 'var(--border)' }}
              />
              <div
                className="text-[10px] font-bold mt-1 transition-colors duration-150"
                style={{ color: progress > 0.6 ? 'var(--std-accent-text, #3b9af2)' : 'transparent' }}
              >놓으면 닫힘</div>
            </div>
          )}
          <div className="flex items-center justify-between px-5 pt-2.5 pb-3.5">
            <div>
              <div className="text-base font-black text-[var(--text)]">{title}</div>
              {count !== undefined && (
                <div className="text-xs text-[var(--text-muted)] mt-0.5">
                  총 {count}{detail.countSuffix ?? '건'}
                </div>
              )}
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full bg-[var(--bg-subtle)] text-[var(--text-sub)] flex items-center justify-center text-base hover:bg-[var(--border)] transition-colors"
            >×</button>
          </div>
        </div>

        {/* 목록 */}
        <div className="overflow-y-auto flex-1 px-3.5 py-2.5 pb-5" style={{ overscrollBehavior: 'contain' }}>
          {rows.length === 0 ? (
            <div className="text-center py-8 text-[var(--text-muted)] text-sm">데이터가 없습니다.</div>
          ) : (
            <div className="flex flex-col gap-1.5">
              {rows.map((row, i) => {
                const c = nameColor(row.primary || String(i));
                return (
                  <div key={i} className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl bg-[var(--bg-subtle)] border border-[var(--border)]">
                    {/* 아바타 */}
                    <div
                      className="w-9 h-9 rounded-full flex-shrink-0 flex items-center justify-center text-sm font-black text-white"
                      style={{ background: `linear-gradient(135deg, ${c}cc, ${c})` }}
                    >
                      {String(row.primary || '?').slice(0, 1)}
                    </div>
                    {/* 텍스트 */}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold text-[var(--text)]">{row.primary}</div>
                      {row.secondary && <div className="text-xs text-[var(--text-sub)] mt-0.5">{row.secondary}</div>}
                      {row.detail && <div className="text-xs text-accent-600 dark:text-accent-400 mt-0.5 font-semibold">{row.detail}</div>}
                    </div>
                    {/* 우측 */}
                    {(row.right || row.rightSub) && (
                      <div className="text-right flex-shrink-0">
                        {row.right && <div className="text-xs font-bold text-[var(--text)]">{row.right}</div>}
                        {row.rightSub && <div className="text-[10px] text-[var(--text-muted)] mt-0.5">{row.rightSub}</div>}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes slideUp     { from { transform:translateY(100%) } to { transform:translateY(0) } }
        @keyframes fadeInScale { from { opacity:0; transform:translate(-50%,-48%) scale(0.96) } to { opacity:1; transform:translate(-50%,-50%) scale(1) } }
      `}</style>
    </>
  );
}
